import type { Program, ReviewStatus, WbProgram } from '@/types'

/**
 * One program as the page lists it: a WB program (ADR-0019) and the crawled
 * programs beneath it, one per degree. "Business" with a BS, a BA and a minor
 * is three crawled rows and one row here.
 *
 * Each member keeps its own status, flag, page and tags; the row shows them
 * combined, and a review action on the row applies to every member. The name,
 * segment and slate mapping are the WB program's. A WB program added by hand
 * may have no members.
 */
export interface ProgramGroup {
  /** The WB program's id. */
  id: string
  wb: WbProgram
  /** The crawled programs shown beneath it, in the API's order: name, then degree. */
  members: Program[]
}

/** The key `useReviewCollection` groups crawled rows on. Unassigned rows are their own. */
export const programGroupKey = (p: Program) => p.wbProgramId ?? `unassigned:${p.id}`

/**
 * One group per WB program that has rows in `rows`, plus every one `keepEmpty`
 * accepts (those `rows` holds none of), by name.
 */
export function groupPrograms(
  wbPrograms: WbProgram[],
  rows: Program[],
  keepEmpty: (wb: WbProgram) => boolean = () => false,
): ProgramGroup[] {
  const members = new Map<string, Program[]>()
  for (const row of rows) {
    if (row.wbProgramId === null) continue
    members.set(row.wbProgramId, [...(members.get(row.wbProgramId) ?? []), row])
  }
  return wbPrograms
    .filter((wb) => members.has(wb.id) || keepEmpty(wb))
    .map((wb) => ({ id: wb.id, wb, members: members.get(wb.id) ?? [] }))
    .sort((a, b) => a.wb.name.localeCompare(b.wb.name, undefined, { sensitivity: 'base' }))
}

/** Distinct non-null values, first appearance wins. */
export function distinct<T>(values: Array<T | null>): T[] {
  return [...new Set(values.filter((v): v is T => v !== null))]
}

/** "BA, BS, Minor". Empty when no member has an extracted degree. */
export function degreeList(group: ProgramGroup): string {
  return distinct(group.members.map((m) => m.degree)).join(', ')
}

/** The members' shared status, `mixed` when they differ, null with no members. */
export function groupStatus(group: ProgramGroup): ReviewStatus | 'mixed' | null {
  const statuses = distinct(group.members.map((m) => m.status))
  if (statuses.length === 0) return null
  return statuses.length === 1 ? statuses[0]! : 'mixed'
}

export const memberIds = (group: ProgramGroup) => group.members.map((m) => m.id)
